import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Trash2, Share2, Phone, MessageSquare, Mail, Clock, Archive } from 'lucide-react';
import { SwipeableRow } from '@/components/SwipeableRow';
import { HunterDeliverabilityShield } from '@/components/leads/HunterDeliverabilityShield';
import { statusColor, scoreBadge, leadAge } from './constants';

export function LeadsMobileCards({ loading, sortedLeads, isViewer, openEdit, openShare, handleDelete, onArchived }) {
  const handleArchive = async (lead) => {
    try {
      await api.updateLead(lead.id, { archived: true });
      toast.success(`Archived “${lead.name}”`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await api.updateLead(lead.id, { archived: false });
              toast.success('Restored');
              onArchived && onArchived();
            } catch { toast.error('Could not restore'); }
          },
        },
        duration: 6000,
      });
      onArchived && onArchived();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not archive');
    }
  };

  return (
    <div className="sm:hidden space-y-2" data-testid="leads-mobile-cards">
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full" /></div>
      ) : sortedLeads.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">No leads found.</div>
      ) : sortedLeads.map(lead => {
        const sb = scoreBadge(lead.score);
        const age = leadAge(lead.created_at, lead.status, lead.closed_at);
        return (
          <SwipeableRow
            key={lead.id}
            className="border border-slate-200 rounded-sm bg-white"
            testId={`lead-card-${lead.id}`}
            onSwipeLeft={isViewer ? null : () => handleArchive(lead)}
            onSwipeRight={isViewer ? null : () => openShare(lead)}
            leftLabel="Archive"
            leftIcon={Archive}
            leftBgClass="bg-amber-600"
            rightLabel="Share"
            rightIcon={Share2}
            rightBgClass="bg-[#0047AB]"
          >
            <div className="flex items-start justify-between gap-2 p-3">
              <Link to={`/leads/${lead.id}`} className="flex-1 min-w-0 active:bg-slate-50 -m-1 p-1 rounded-sm">
                <p className="text-sm font-semibold text-slate-900 truncate">{lead.name}</p>
                <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                  <span className="truncate">{lead.company || '---'}{lead.email ? ` · ${lead.email}` : ''}</span>
                  {lead.email && <HunterDeliverabilityShield verification={lead.email_verification} />}
                </p>
                <div className="flex items-center gap-1 mt-2 flex-wrap">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${statusColor(lead.status)}`}>{lead.status}</span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${sb.cls}`}>{sb.label}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5 ${age.cls}`}><Clock className="w-2.5 h-2.5" />{age.label}</span>
                  {lead.shared_with?.length > 0 && <Share2 className="w-3 h-3 text-[#0047AB]" />}
                </div>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-11 w-11 p-0 shrink-0 -mr-1 -mt-1" aria-label="Lead actions" data-testid={`lead-card-actions-${lead.id}`}>
                    <MoreHorizontal className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to={`/leads/${lead.id}`}><Eye className="w-4 h-4 mr-2" /> View</Link></DropdownMenuItem>
                  {!isViewer && <DropdownMenuItem onClick={() => openEdit(lead)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>}
                  {!isViewer && <DropdownMenuItem onClick={() => openShare(lead)}><Share2 className="w-4 h-4 mr-2" /> Share</DropdownMenuItem>}
                  {!isViewer && <DropdownMenuItem onClick={() => handleArchive(lead)} data-testid={`lead-card-archive-${lead.id}`}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>}
                  {!isViewer && <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {(lead.phone || lead.email) && (
              <div className="flex items-stretch border-t border-slate-100">
                {lead.phone && (
                  <a href={`tel:${lead.phone}`} onClick={(e) => { e.stopPropagation(); api.logLeadTouch(lead.id, 'call'); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-xs font-medium text-[#0047AB] active:bg-[#EFF6FF]" data-testid={`lead-card-call-${lead.id}`} aria-label="Call">
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                )}
                {lead.phone && (
                  <a href={`sms:${lead.phone}`} onClick={(e) => { e.stopPropagation(); api.logLeadTouch(lead.id, 'sms'); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-xs font-medium text-emerald-700 active:bg-emerald-50 border-l border-slate-100" data-testid={`lead-card-text-${lead.id}`} aria-label="Text">
                    <MessageSquare className="w-3.5 h-3.5" /> Text
                  </a>
                )}
                {lead.email && (
                  <a href={`mailto:${lead.email}`} onClick={(e) => { e.stopPropagation(); api.logLeadTouch(lead.id, 'email'); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-xs font-medium text-slate-700 active:bg-slate-50 border-l border-slate-100" data-testid={`lead-card-email-${lead.id}`} aria-label="Email">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                )}
              </div>
            )}
          </SwipeableRow>
        );
      })}
    </div>
  );
}
